
import React, { useState } from 'react';
import type { PrivacyZone, UserProfile } from '../types';
import { PlusIcon, TrashIcon, MapPinIcon, PencilIcon, XIcon } from './icons/PlaybackIcons';
import PrivacyZoneMap from './PrivacyZoneMap';

interface PrivacyZoneManagerProps {
    userProfile: UserProfile;
    onChange: (zones: PrivacyZone[]) => void;
}

const PrivacyZoneManager: React.FC<PrivacyZoneManagerProps> = ({ userProfile, onChange }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingZone, setEditingZone] = useState<Partial<PrivacyZone>>({
        radius: 402.34 // Default 0.25 miles in meters
    });
    
    // Derived zones list
    const zones = userProfile.privacyZones || [];

    const handleOpenAdd = () => {
        setEditingZone({
            id: undefined,
            name: '',
            lat: 0,
            lon: 0,
            radius: 402.34 // 0.25 miles default
        });
        setIsModalOpen(true);
    };

    const handleEdit = (zone: PrivacyZone) => {
        setEditingZone({ ...zone });
        setIsModalOpen(true);
    };

    const handleDelete = (id: string) => {
        if (confirm("Are you sure you want to delete this privacy zone?")) {
            const updated = zones.filter(z => z.id !== id);
            onChange(updated);
        }
    };

    const handleSave = () => {
        if (!editingZone.name || !editingZone.lat || !editingZone.lon || !editingZone.radius) {
            alert("Please set a name and location for the zone.");
            return;
        }

        const newZone: PrivacyZone = {
            id: editingZone.id || `zone-${Date.now()}`,
            name: editingZone.name,
            lat: editingZone.lat,
            lon: editingZone.lon,
            radius: editingZone.radius
        };

        let updatedZones;
        if (editingZone.id) {
            updatedZones = zones.map(z => z.id === editingZone.id ? newZone : z);
        } else {
            updatedZones = [...zones, newZone];
        }

        onChange(updatedZones);
        setIsModalOpen(false);
    };

    const milesToMeters = (miles: number) => miles * 1609.34;
    const metersToMiles = (meters: number) => meters / 1609.34;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-white">Privacy Zones</h3>
                <button 
                    type="button"
                    onClick={handleOpenAdd}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-cyan-600 rounded-md hover:bg-cyan-500 transition-colors"
                >
                    <PlusIcon className="w-4 h-4" />
                    Add Zone
                </button>
            </div>
            
            <p className="text-sm text-gray-400">
                Define circular zones (e.g. Home, Work) where your track data will be hidden from the map.
            </p>

            {zones.length === 0 ? (
                <div className="p-6 bg-gray-900/50 rounded-lg border border-gray-700 border-dashed text-center text-gray-500 text-sm">
                    No privacy zones set. Your start/finish locations are visible.
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3">
                    {zones.map(zone => (
                        <div key={zone.id} className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg border border-gray-600">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-500/20 text-red-400 rounded-full">
                                    <MapPinIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="font-bold text-white">{zone.name}</div>
                                    <div className="text-xs text-gray-400">
                                        Radius: {metersToMiles(zone.radius).toFixed(2)} mi
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button 
                                    type="button"
                                    onClick={() => handleEdit(zone)}
                                    className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                                >
                                    <PencilIcon className="w-4 h-4" />
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => handleDelete(zone.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-900/30 rounded transition-colors"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[3000] p-4 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}>
                    <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col border border-gray-700 relative" onClick={e => e.stopPropagation()}>
                        
                        {/* Header */}
                        <div className="flex justify-between items-center p-4 border-b border-gray-700">
                            <h2 className="text-xl font-bold text-white">{editingZone.id ? 'Edit Privacy Zone' : 'Create Privacy Zone'}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                                <XIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-grow p-4 flex flex-col gap-4 overflow-hidden">
                            <div className="flex gap-4 items-end">
                                <div className="flex-grow">
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Zone Name</label>
                                    <input 
                                        type="text"
                                        placeholder="e.g. Home"
                                        value={editingZone.name}
                                        onChange={(e) => setEditingZone(prev => ({ ...prev, name: e.target.value }))}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                    />
                                </div>
                            </div>

                            <div className="flex-grow border border-gray-600 rounded-lg overflow-hidden">
                                <PrivacyZoneMap 
                                    lat={editingZone.lat || 0} 
                                    lon={editingZone.lon || 0} 
                                    radiusMiles={metersToMiles(editingZone.radius || 402.34)}
                                    onLocationChange={(lat, lon) => setEditingZone(prev => ({ ...prev, lat, lon }))}
                                    onRadiusChange={(miles) => setEditingZone(prev => ({ ...prev, radius: milesToMeters(miles) }))}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-700 flex justify-end gap-3 bg-gray-800/50">
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 text-gray-300 hover:text-white font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSave}
                                className="px-6 py-2 bg-cyan-600 text-white font-bold rounded-lg hover:bg-cyan-500 shadow-lg"
                            >
                                Save Zone
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrivacyZoneManager;
